import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Facebook, Youtube } from "lucide-react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Seo from "@/components/Seo";
import BottomNav from "@/components/BottomNav";
import Ricette from "@/sections/Ricette";
import Biscotti from "@/sections/Biscotti";
import Tecniche from "@/sections/Tecniche";
import Galleria from "@/sections/Galleria";
import Corso from "@/sections/Corso";
import { SOCIALS } from "@/data/content";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, "facebook-page": Facebook, youtube: Youtube };

const SECTIONS = { ricette: Ricette, biscotti: Biscotti, tecniche: Tecniche, galleria: Galleria, corso: Corso };

function App() {
  const [tab, setTab] = useState("ricette");
  const lenisRef = useRef(null);

  useEffect(() => {
    // Su iPhone/touch Lenis rende lo scroll a scatti: lasciamo lo scroll nativo
    if (window.matchMedia("(pointer: coarse)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const changeTab = (id) => {
    setTab(id);
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  };

  const scrollToCalculator = () => {
    const el = document.getElementById("calcolatore");
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ActiveSection = SECTIONS[tab];

  return (
    <div className="min-h-screen bg-crema text-cacao">
      <Seo
        title="Le Allegre Pasticcione — Corsi di Cake Design e Biscotti in Pasta di Zucchero"
        siteName="Le Allegre Pasticcione"
        description="Impara il cake design con Giorgia Perletta: ricetta perfetta della pasta frolla con calcolatore delle dosi, biscotto della settimana, tecniche di modellaggio 3D e il corso completo su Hotmart."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "DolceArte",
          description: "Piattaforma educativa di cake design: ricette, tutorial di biscotti decorati e corsi di modellaggio in pasta di zucchero.",
          inLanguage: "it",
        }}
      />
      <div className="grain pointer-events-none fixed inset-0 z-40 opacity-[0.05]" />
      <header className="flex justify-center pt-6">
        <img
          src="/images/logo.png"
          alt="Le Allegre Pasticcione — Giorgia Perletta"
          data-testid="brand-logo"
          className="h-14 w-auto sm:h-16"
        />
      </header>
      <main className="pb-32">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <ActiveSection onExplore={scrollToCalculator} />
          </motion.div>
        </AnimatePresence>
      </main>
      <footer className="border-t border-pink-100 bg-burro/50 px-5 pb-32 pt-10 text-center">
        <img
          src="/images/logo.png"
          alt="Le Allegre Pasticcione — Giorgia Perletta"
          data-testid="footer-logo"
          className="mx-auto h-24 w-auto"
        />
        <p className="mt-1 text-xs text-cacao-light">
          di Giorgia Perletta · cake design & pasta di zucchero, con amore
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          {SOCIALS.map((s) => {
            const Icon = SOCIAL_ICONS[s.id];
            return (
              <a
                key={s.id}
                data-testid={`social-${s.id}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                title={s.label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-pink-200 bg-white text-cacao-muted transition-all hover:-translate-y-0.5 hover:border-rosa hover:text-rosa-dark"
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
        </div>
      </footer>
      <BottomNav active={tab} onChange={changeTab} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
